import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { controlClass } from '../../../components/ui/Field';
import TemplateEditor from '../../../components/ui/TemplateEditor';
import Combo from '../../../components/ui/Combo';
import { METHODS } from '../../../utils/toolDefinition';
import { useInsert, useScope } from './InsertContext';
import { previewParts } from '../../../utils/templatePreview';
import { engineService } from '../../../services/engineService';

// Campo que acepta datos de la herramienta con {{…}}: editor con autocompletado (al escribir «{{», «|» o «.» dentro de {{= }}) y el
// botón «{ }» que lista los datos disponibles (entradas y resultados de pasos anteriores) y los inserta donde está el cursor.
export default function ExprInput({ value, onChange, vars, multiline = false, placeholder, ariaLabel, preview = true }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const btn = useRef(null);
  const menu = useRef(null);
  const latest = useRef(value);
  latest.current = value;
  const { register } = useInsert();
  const [over, setOver] = useState(false);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('datos');
  const [pos, setPos] = useState(null);
  // El menú vive en el body (fixed): no lo recorta el panel que hace scroll y se acomoda arriba o abajo del botón, donde haya más lugar.
  const place = () => {
    const r = btn.current?.getBoundingClientRect(); if (!r) return;
    const below = window.innerHeight - r.bottom - 12; const above = r.top - 12;
    const down = below >= 300 || below >= above;
    const width = Math.min(400, window.innerWidth - 24);
    setPos({ left: Math.max(12, Math.min(r.right - width, window.innerWidth - width - 12)), width, maxHeight: Math.max(180, Math.min(560, down ? below : above)), ...(down ? { top: r.bottom + 4 } : { bottom: window.innerHeight - r.top + 4 }) });
  };
  useEffect(() => {
    if (!open) return undefined;
    place(); setQ(''); setTab('datos');
    const close = (e) => { if (!box.current?.contains(e.target) && !menu.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); btn.current?.focus(); } };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc, true);
    window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc, true); window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);
  const insert = (path) => insertText(`{{${path}}}`);
  // Un método va dentro de una plantilla: si el cursor está justo después de «}}», se mete antes de cerrar; si no, se agrega al final
  // de la última plantilla del texto.
  const method = (m) => {
    const v = value || ''; const add = ` | ${m.name}${m.arg ? `:${m.arg}` : ''}`;
    const at = ref.current?.cursor() ?? v.length;
    const close = v.lastIndexOf('}}', at);
    if (close < 0) { setOpen(false); return; }
    onChange(v.slice(0, close) + add + v.slice(close));
    setOpen(false);
  };
  const insertText = (token) => {
    if (ref.current) ref.current.insert(token); else onChange((latest.current || '') + token);
    setOpen(false);
  };
  const hasTemplate = /\{\{/.test(value || '');
  const needle = q.trim().toLowerCase();
  const hit = (...t) => !needle || t.some((x) => String(x || '').toLowerCase().includes(needle));
  const shown = (vars || []).filter((v) => hit(v.label, v.path, v.hint));
  const groups = [...new Set(shown.map((v) => v.group))];
  const methods = METHODS.filter((m) => hit(m.label, m.name, m.example));
  const onSearchKey = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (tab === 'datos' && shown[0]) insert(shown[0].path); else if (tab === 'metodos' && methods[0]) method(methods[0]);
  };
  return (
    <div className="relative" ref={box}>
      <div onDragOver={(e) => { if (e.dataTransfer.types.includes('text/plain')) setOver(true); }} onDragLeave={() => setOver(false)} onDrop={() => setOver(false)}>
        <TemplateEditor ref={ref} value={value ?? ''} onChange={onChange} vars={vars} multiline={multiline} placeholder={placeholder} ariaLabel={ariaLabel}
          warnUnknownRoots={Boolean(vars?.length)} minHeight={multiline ? 76 : undefined} className={over ? 'ring-2 ring-live' : ''}
          onFocus={() => register({ el: ref.current?.view?.dom, insert: insertText })} />
      </div>
      <button ref={btn} type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="menu" aria-label="Insertar un dato" title="Insertar un dato"
        className="absolute right-1.5 top-1.5 h-7 px-2 rounded-lg bg-panel/90 text-[12px] font-mono font-semibold text-fg-dim hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">{'{ }'}</button>
      {open && pos && createPortal(
        <div ref={menu} role="menu" aria-label="Datos disponibles" className="fixed z-[80] flex flex-col rounded-xl bg-panel ring-1 ring-line" style={{ ...pos, boxShadow: 'var(--shadow)' }}>
          <div className="p-2 border-b border-line space-y-2 shrink-0">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey} placeholder="Buscar un dato…" aria-label="Buscar un dato" spellCheck={false}
              className="w-full h-9 rounded-lg bg-raised/60 border border-line px-3 text-[13px] text-fg placeholder:text-fg-faint focus:outline-none focus-visible:border-live focus-visible:ring-2 focus-visible:ring-live/30" />
            {hasTemplate && (
              <div role="tablist" aria-label="Qué insertar" className="flex gap-1">
                {[{ k: 'datos', t: 'Datos' }, { k: 'metodos', t: 'Métodos' }].map((o) => (
                  <button key={o.k} type="button" role="tab" aria-selected={tab === o.k} onClick={() => setTab(o.k)}
                    className={`h-7 px-3 rounded-full text-[12px] font-semibold ${tab === o.k ? 'bg-raised text-fg ring-1 ring-line' : 'text-fg-dim hover:text-fg'}`}>{o.t}</button>
                ))}
              </div>
            )}
          </div>
          <div className="overflow-y-auto p-1.5 min-h-0">
            {tab === 'datos' && (
              <>
                {!vars?.length && <p className="px-2.5 py-2 text-[12.5px] text-fg-dim">Todavía no hay datos: agrega datos de entrada en el primer paso.</p>}
                {Boolean(vars?.length) && !shown.length && <p className="px-2.5 py-2 text-[12.5px] text-fg-dim">Ningún dato coincide con «{q}».</p>}
                {groups.map((g) => (
                  <div key={g}>
                    <p className="sticky top-0 z-10 bg-panel px-2.5 pt-2 pb-1 text-[11.5px] font-semibold text-fg-faint">{g}</p>
                    {shown.filter((v) => v.group === g).map((v) => (
                      <button key={v.path} type="button" role="menuitem" onClick={() => insert(v.path)} title={`{{${v.path}}}${v.hint ? ` = ${v.hint}` : ''}`}
                        className="w-full flex items-baseline gap-2 text-left px-2.5 py-1.5 rounded-lg hover:bg-raised focus-visible:outline-none focus-visible:bg-raised">
                        <span className="shrink-0 max-w-[55%] truncate text-[13px] text-fg">{v.label}</span>
                        <span className="min-w-0 flex-1 truncate text-right text-[11.5px] font-mono text-fg-faint">{v.hint ?? v.path}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </>
            )}
            {tab === 'metodos' && (
              <>
                <p className="px-2.5 pt-1 pb-1 text-[11.5px] text-fg-faint">Se aplican a la plantilla que está antes del cursor.</p>
                {!methods.length && <p className="px-2.5 py-2 text-[12.5px] text-fg-dim">Ningún método coincide con «{q}».</p>}
                {methods.map((m) => (
                  <button key={m.name} type="button" role="menuitem" onClick={() => method(m)} title={`| ${m.name}${m.arg ? `:${m.arg}` : ''}`}
                    className="w-full flex items-baseline gap-2 text-left px-2.5 py-1.5 rounded-lg hover:bg-raised focus-visible:outline-none focus-visible:bg-raised">
                    <span className="shrink-0 max-w-[60%] truncate text-[13px] text-fg">{m.label}</span>
                    <span className="min-w-0 flex-1 truncate text-right text-[11.5px] font-mono text-fg-faint">{m.example || `| ${m.name}`}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>, document.body)}
      {preview && /\{\{/.test(value || '') && <Preview template={value} multiline={multiline} />}
    </div>
  );
}

/**
 * El texto ya armado con los datos de la última prueba (o los fijados): lo que vino de un dato va resaltado; lo que falta, marcado.
 * Si hay código {{= … }}, el texto completo lo calcula el motor (QuickJS) con esos mismos datos.
 */
function Preview({ template, multiline }) {
  const scope = useScope();
  const local = scope ? previewParts(template, scope) : null;
  const needsEngine = Boolean(local?.some((p) => p.code));
  const [remote, setRemote] = useState(null);
  useEffect(() => {
    if (!needsEngine) { setRemote(null); return undefined; }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      engineService.renderTemplate(template, scope, { signal: ctrl.signal })
        .then((r) => setRemote({ parts: r.parts }))
        .catch((e) => { if (e.code !== 'aborted') setRemote({ error: e.message }); });
    }, 400);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [needsEngine, template, scope]);
  if (!scope) return <p className="mt-1 text-[11.5px] text-fg-faint">Prueba la herramienta (o fija datos) para ver aquí el texto ya armado.</p>;
  const parts = needsEngine ? remote?.parts : local;
  const missing = (parts || []).filter((p) => p.missing || p.error).length;
  return (
    <div className="mt-1.5 rounded-lg bg-raised/60 ring-1 ring-line px-2.5 py-1.5" aria-label="Vista previa con datos">
      <p className="text-[10.5px] font-semibold text-fg-faint mb-0.5">Así queda{missing ? ` · ${missing === 1 ? '1 dato no existe o falló' : `${missing} datos no existen o fallaron`}` : ''}{needsEngine && !remote ? ' · calculando el código…' : ''}</p>
      {remote?.error && <p className="text-[12px] text-signal">{remote.error}</p>}
      {parts && (
        <p className={`text-[12.5px] text-fg break-words ${multiline ? 'whitespace-pre-wrap' : 'truncate'}`}>
          {parts.map((p, i) => (p.from === undefined ? <span key={i}>{p.text}</span>
            : p.error ? <mark key={i} title={`${p.from}: ${p.error}`} className="rounded bg-signal-soft px-0.5 text-signal">{p.error}</mark>
            : p.missing ? <mark key={i} title={`${p.from}: no hay ese dato en la prueba`} className="rounded bg-caution-soft px-0.5 text-caution">{p.text || p.from}</mark>
            : <mark key={i} title={`Viene de ${p.from}`} className="rounded-md bg-live-soft px-1 font-semibold text-live ring-1 ring-inset ring-live/50 [box-decoration-break:clone]">{p.text}</mark>))}
        </p>
      )}
    </div>
  );
}

/**
 * Lista de pares nombre → valor (columnas, encabezados, campos). `keyOptions`: sugerencias para un nombre libre (encabezados HTTP,
 * campos propios). `keyChoices`: lista CERRADA ([{ value, hint, tags }]): el nombre se elige de ahí y no se escribe (columnas de una
 * tabla, encabezados de una hoja); cada nombre se puede usar una sola vez.
 */
export function KVList({ items, onChange, vars, keyLabel = 'Nombre', valueLabel = 'Valor', keyOptions, keyChoices, addLabel = 'Agregar', idPrefix, disabled = false }) {
  const list = items || [];
  const set = (i, k, v) => onChange(list.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const dl = keyOptions?.length ? `${idPrefix}-keys` : undefined;
  return (
    <div className="space-y-2">
      {dl && <datalist id={dl}>{keyOptions.map((o) => <option key={o} value={o} />)}</datalist>}
      {list.map((kv, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto] gap-2 items-start">
          {keyChoices ? (
            <Combo value={kv.key} onChange={(v) => set(i, 'key', v)} ariaLabel={`${keyLabel} ${i + 1}`} placeholder={`Elige ${keyLabel}`}
              options={keyChoices.filter((o) => o.value === kv.key || !list.some((x) => x.key === o.value))} />
          ) : (
            <input value={kv.key} onChange={(e) => set(i, 'key', e.target.value)} aria-label={`${keyLabel} ${i + 1}`} placeholder={keyLabel} list={dl} spellCheck={false}
              className={`${controlClass} h-10 font-mono text-[12.5px]`} />
          )}
          <ExprInput value={kv.value} onChange={(v) => set(i, 'value', v)} vars={vars} ariaLabel={`${valueLabel} de ${kv.key || i + 1}`} placeholder={valueLabel} />
          <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} aria-label={`Quitar ${kv.key || 'fila'}`}
            className="h-10 w-9 grid place-items-center rounded-lg text-fg-faint hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60"><FaTimes aria-hidden className="h-3 w-3" /></button>
        </div>
      ))}
      <button type="button" disabled={disabled || (keyChoices && list.length >= keyChoices.length)} onClick={() => onChange([...list, { key: '', value: '' }])}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12.5px] font-semibold text-fg-dim ring-1 ring-line hover:text-fg hover:ring-fg/20 disabled:opacity-40 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
        <FaPlus aria-hidden className="h-2.5 w-2.5" />{addLabel}
      </button>
    </div>
  );
}
